import { useState, useEffect, useDeferredValue } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { X, Search, Calendar, User, Building2 } from 'lucide-react';
import { apiHelper } from '@services/api';
import Button from '@components/common/Button';

const TicketFilters = ({ searchParams, setSearchParams }) => {
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    priority: searchParams.get('priority') || '',
    assigned_to: searchParams.get('assigned_to') || '',
    module_id: searchParams.get('module_id') || '',
    branch_id: searchParams.get('branch_id') || '',
    created_from: searchParams.get('created_from') || '',
    created_to: searchParams.get('created_to') || ''
  });

  const deferredSearch = useDeferredValue(filters.search);

  const { data: filterOptions, isLoading } = useQuery(
    ['filter-options'],
    async () => {
      const [modules, branches, users] = await Promise.all([
        apiHelper.get('/modules').then(res => res.data),
        apiHelper.get('/branches').then(res => res.data),
        apiHelper.get('/users').then(res => res.data)
      ]);
      return { modules, branches, users };
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const statusOptions = [
    { label: 'Not Started', value: 'not_started' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'On Hold', value: 'on_hold' },
    { label: 'Completed', value: 'completed' },
    { label: 'Reopened', value: 'reopened' },
    { label: 'Reclosed', value: 'reclosed' },
    { label: 'Cancelled', value: 'cancelled' },
    { label: 'Escalated', value: 'escalated' }
  ];

  const priorityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
    { label: 'Critical', value: 'critical' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    const newParams = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value) newParams.set(key, value);
    });

    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    const cleared = {
      search: '',
      status: '',
      priority: '',
      assigned_to: '',
      module_id: '',
      branch_id: '',
      created_from: '',
      created_to: ''
    };
    setFilters(cleared);
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = Object.values(filters).some(val => val !== '');

  return (
    <motion.div
      className="bg-white rounded-xl shadow-sm border p-6"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<X className="w-4 h-4" />}
            onClick={clearFilters}
            className="text-error-600 hover:text-error-700 hover:bg-error-50"
          >
                        Clear All
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Search */}
        <InputWithIcon
          label="Search"
          icon={Search}
          value={filters.search}
          onChange={e => handleFilterChange('search', e.target.value)}
          placeholder="Search tickets..."
        />

        {/* Status */}
        <SelectBox
          label="Status"
          value={filters.status}
          onChange={val => handleFilterChange('status', val)}
          options={statusOptions}
        />

        {/* Priority */}
        <SelectBox
          label="Priority"
          value={filters.priority}
          onChange={val => handleFilterChange('priority', val)}
          options={priorityOptions}
        />

        {/* Assigned To */}
        <SelectBox
          label="Assignee"
          value={filters.assigned_to}
          onChange={val => handleFilterChange('assigned_to', val)}
          icon={User}
          options={[
            { value: '', label: 'All Assignees' },
            { value: 'me', label: 'My Tickets' },
            { value: 'unassigned', label: 'Unassigned' },
            ...(filterOptions?.users || []).map(u => ({
              value: u.id,
              label: `${u.first_name} ${u.last_name}`
            }))
          ]}
          loading={isLoading}
        />

        {/* Module */}
        <SelectBox
          label="Module"
          value={filters.module_id}
          onChange={val => handleFilterChange('module_id', val)}
          options={
            filterOptions?.modules?.map(m => ({
              value: m.id,
              label: m.name
            })) || []
          }
          placeholder="All Modules"
          loading={isLoading}
        />

        {/* Branch */}
        <SelectBox
          label="Branch"
          value={filters.branch_id}
          onChange={val => handleFilterChange('branch_id', val)}
          icon={Building2}
          options={
            filterOptions?.branches?.map(b => ({
              value: b.id,
              label: b.name
            })) || []
          }
          placeholder="All Branches"
          loading={isLoading}
        />

        {/* Created From */}
        <InputWithIcon
          label="Created From"
          icon={Calendar}
          type="date"
          value={filters.created_from}
          onChange={e => handleFilterChange('created_from', e.target.value)}
        />

        {/* Created To */}
        <InputWithIcon
          label="Created To"
          icon={Calendar}
          type="date"
          value={filters.created_to}
          onChange={e => handleFilterChange('created_to', e.target.value)}
        />
      </div>

      <div className="flex items-center gap-3">
        <Button variant="primary" onClick={applyFilters}>
                    Apply Filters
        </Button>
        <Button variant="outline" onClick={clearFilters} disabled={!hasActiveFilters}>
                    Reset
        </Button>
        {hasActiveFilters && <p className="text-sm text-gray-500">Active filters applied</p>}
      </div>
    </motion.div>
  );
};

/* ----- Subcomponents ------ */

const InputWithIcon = ({ label, icon: Icon, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
      )}
      <input
        {...props}
        className={'w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors'}
      />
    </div>
  </div>
);

const SelectBox = ({
  label,
  icon: Icon,
  value,
  onChange,
  options = [],
  loading = false,
  placeholder = 'All'
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
      )}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={loading}
        className={`w-full ${Icon ? 'pl-10' : 'pl-3'} pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors`}
      >
        <option value="">{placeholder}</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  </div>
);

export default TicketFilters;
